import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { REALISASI, formatRupiah, realisasiOf, timLabel } from "@/lib/agenda";
import AgendaImport from "@/components/apik/AgendaImport";

const inputCls =
  "mt-2 w-full rounded-xl border border-[#E3EBF3] px-4 py-3 text-sm outline-none transition focus:border-[#1479BF]";
const labelCls =
  "text-[11px] font-bold uppercase tracking-[0.14em] text-[#0B2440]";

export default function EmonevView({ user }) {
  const isAdmin = user?.role === "ppid";
  const [groups, setGroups] = useState(null);
  const [group, setGroup] = useState(isAdmin ? "" : user?.key || "");
  const [entries, setEntries] = useState(null);
  const [title, setTitle] = useState("");
  const [realisasi, setRealisasi] = useState("belum");
  const [waktu, setWaktu] = useState("");
  const [anggaran, setAnggaran] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const data = await api("/api/admin/accounts");
        setGroups(data);
        if (data.length > 0) setGroup((g) => g || data[0].key);
      } catch (err) {
        toast(formatApiError(err.message));
      }
    })();
  }, [isAdmin]);

  const load = useCallback(async () => {
    if (!group) return;
    try {
      setEntries(await api(`/api/agenda/${group}`));
    } catch (err) {
      toast(formatApiError(err.message));
    }
  }, [group]);

  useEffect(() => {
    load();
  }, [load]);

  const groupLabel =
    (groups || []).find((g) => g.key === group)?.label || user?.label || "";
  const pj = timLabel(groupLabel);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast("Nama kegiatan wajib diisi.");
      return;
    }
    setSaving(true);
    try {
      await api(`/api/agenda/${group}`, {
        method: "POST",
        body: JSON.stringify({
          title,
          realisasi,
          waktu,
          anggaran: anggaran === "" ? null : Number(anggaran),
        }),
      });
      setTitle("");
      setRealisasi("belum");
      setWaktu("");
      setAnggaran("");
      toast("Kegiatan berhasil ditambahkan dan tampil publik.");
      await load();
    } catch (err) {
      toast(formatApiError(err.message));
    } finally {
      setSaving(false);
    }
  };

  const updateRealisasi = async (id, val) => {
    try {
      await api(`/api/agenda/${group}/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ realisasi: val }),
      });
      toast(`Realisasi diperbarui: ${realisasiOf(val).label}`);
      await load();
    } catch (err) {
      toast(formatApiError(err.message));
    }
  };

  const remove = async (id) => {
    try {
      await api(`/api/agenda/${group}/${id}`, { method: "DELETE" });
      toast("Kegiatan dihapus.");
      await load();
    } catch (err) {
      toast(formatApiError(err.message));
    }
  };

  const togglePublish = async (e) => {
    const next = e.published === false;
    try {
      await api(`/api/agenda/${group}/${e.id}`, {
        method: "PATCH",
        body: JSON.stringify({ published: next }),
      });
      toast(next ? "Kegiatan dipublikasikan." : "Publikasi dibatalkan.");
      await load();
    } catch (err) {
      toast(formatApiError(err.message));
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-semibold text-[#0B2440] md:text-4xl">
        Rekap Program Kegiatan
      </h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-500">
        Rekap program dan Agenda Kegiatan yang tampil pada halaman publik
        Pengumuman — Informasi Pelaksanaan Program dan Kegiatan.
        {!isAdmin &&
          " Kegiatan yang Anda tambahkan akan tampil publik setelah dipublikasikan oleh Admin PPID."}
      </p>

      {isAdmin && <AgendaImport onDone={load} />}

      {isAdmin && (
        <label className="mt-6 block max-w-sm">
          <span className={labelCls}>Kelompok Pengelola</span>
          <select
            data-testid="emonev-group-select"
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            className={`${inputCls} bg-white text-slate-700`}
          >
            {(groups || []).map((g) => (
              <option key={g.key} value={g.key}>
                {g.label}
              </option>
            ))}
          </select>
        </label>
      )}

      <form
        data-testid="agenda-form"
        onSubmit={submit}
        className="mt-8 rounded-3xl border border-[#E3EBF3] bg-white p-6 md:p-8"
      >
        <h2 className="font-display text-xl font-semibold text-[#0B2440]">
          Tambah Agenda Kegiatan
        </h2>
        <div className="mt-5 grid gap-5 md:grid-cols-[1fr_280px]">
          <label className="block">
            <span className={labelCls}>Nama Kegiatan</span>
            <input
              data-testid="agenda-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Contoh: Sosialisasi Akreditasi PTS 2026"
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>Penanggungjawab</span>
            <input
              data-testid="agenda-pj-input"
              value={pj}
              readOnly
              className={`${inputCls} cursor-not-allowed bg-[#F3F7FB] text-slate-500`}
            />
          </label>
        </div>
        <div className="mt-5 grid gap-5 md:grid-cols-3">
          <label className="block">
            <span className={labelCls}>Realisasi</span>
            <select
              data-testid="agenda-realisasi-select"
              value={realisasi}
              onChange={(e) => setRealisasi(e.target.value)}
              className={`${inputCls} bg-white`}
            >
              {REALISASI.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>Waktu Pelaksanaan</span>
            <input
              data-testid="agenda-waktu-input"
              value={waktu}
              onChange={(e) => setWaktu(e.target.value)}
              placeholder="Contoh: Maret 2026 / Triwulan II"
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className={labelCls}>Jumlah Anggaran (Rp)</span>
            <input
              type="text"
              inputMode="numeric"
              data-testid="agenda-anggaran-input"
              value={anggaran === "" ? "" : Number(anggaran).toLocaleString("id-ID")}
              onChange={(e) => setAnggaran(e.target.value.replace(/\D/g, ""))}
              placeholder="Contoh: 25.000.000"
              className={inputCls}
            />
          </label>
        </div>
        <button
          type="submit"
          data-testid="agenda-save-button"
          disabled={saving}
          className="mt-6 rounded-full bg-[#1479BF] px-7 py-3 text-xs font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-[#0B2440] disabled:opacity-60"
        >
          {saving ? "MENYIMPAN..." : "TAMBAH KEGIATAN"}
        </button>
      </form>

      <h2 className="mt-12 font-display text-2xl font-semibold text-[#0B2440]">
        Daftar Kegiatan
      </h2>
      {entries === null ? (
        <p className="mt-4 text-sm font-semibold text-slate-500">Memuat...</p>
      ) : entries.length === 0 ? (
        <p
          data-testid="agenda-empty"
          className="mt-4 rounded-2xl border border-dashed border-[#E3EBF3] bg-white px-6 py-8 text-center text-sm text-slate-400"
        >
          Belum ada kegiatan. Tambahkan kegiatan pertama melalui form di atas.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-2xl border border-[#E3EBF3] bg-white">
          <table data-testid="agenda-admin-table" className="w-full min-w-[820px] text-left text-xs">
            <thead>
              <tr className="border-b border-[#E3EBF3] bg-[#FBFDFF] text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
                <th className="w-12 px-4 py-3">No</th>
                <th className="px-4 py-3">Nama Kegiatan</th>
                <th className="px-4 py-3">Penanggungjawab</th>
                <th className="px-4 py-3">Realisasi</th>
                <th className="px-4 py-3">Waktu Pelaksanaan</th>
                <th className="px-4 py-3 text-right">Jumlah Anggaran</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e, idx) => (
                <tr
                  key={e.id}
                  data-testid={`agenda-entry-${e.id}`}
                  className="border-b border-[#E3EBF3] last:border-b-0"
                >
                  <td className="px-4 py-3 font-semibold text-slate-400">{idx + 1}</td>
                  <td className="px-4 py-3 font-bold text-[#0B2440]">{e.title}</td>
                  <td className="px-4 py-3 text-slate-600">{pj}</td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`agenda-realisasi-${e.id}`}
                      value={e.realisasi || "belum"}
                      onChange={(ev) => updateRealisasi(e.id, ev.target.value)}
                      className={`rounded-full border-0 px-2.5 py-1 text-[10px] font-bold outline-none ${realisasiOf(e.realisasi).cls}`}
                    >
                      {REALISASI.map((r) => (
                        <option key={r.key} value={r.key}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{e.waktu || e.date || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-[#0B2440]">
                    {formatRupiah(e.anggaran)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {isAdmin ? (
                        <button
                          data-testid={`agenda-publish-${e.id}`}
                          onClick={() => togglePublish(e)}
                          title={
                            e.published === false
                              ? "Tampilkan di halaman publik"
                              : "Klik untuk membatalkan publikasi"
                          }
                          className={`rounded-full px-3 py-1 text-[10px] font-bold transition-colors duration-300 ${
                            e.published === false
                              ? "bg-[#1479BF] text-white hover:bg-[#0B2440]"
                              : "border border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                          }`}
                        >
                          {e.published === false ? "PUBLIKASIKAN" : "TERPUBLIKASI"}
                        </button>
                      ) : (
                        <span
                          data-testid={`agenda-pubstatus-${e.id}`}
                          className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${
                            e.published === false
                              ? "bg-amber-50 text-amber-600"
                              : "bg-emerald-50 text-emerald-600"
                          }`}
                        >
                          {e.published === false ? "Menunggu Publikasi" : "Terpublikasi"}
                        </span>
                      )}
                      <button
                        data-testid={`agenda-delete-${e.id}`}
                        onClick={() => remove(e.id)}
                        className="rounded-full border border-[#E3EBF3] px-3 py-1 text-[10px] font-bold text-red-500 transition-colors duration-300 hover:bg-red-50"
                      >
                        HAPUS
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
